import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { recalcLine, round2, rateToCode } from '../utils/lineMath';

// Register community modules for AG Grid (error 272 fix)
ModuleRegistry.registerModules([AllCommunityModule]);

const BulkLineItemsGrid = ({ lineItems = [], onChange }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [quickFilterText, setQuickFilterText] = useState('');

  useEffect(() => {
    const withIds = (lineItems || []).map((item, idx) => ({
      _rowId: item?.id ?? idx,
      ...item,
    }));
    setRowData(withIds);
  }, [lineItems]);

  const vatValues = useMemo(() => ['0', '5', '20'], []);

  const currencyFormatter = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '£0.00';
    return `£${round2(n).toFixed(2)}`;
  };

  const syncRowsFromGrid = () => {
    const rows = [];
    gridRef.current?.api?.forEachNode((node) => {
      rows.push(node.data);
    });
    setRowData(rows);
    onChange?.(rows);
  };

  const columnDefs = useMemo(() => [
    {
      headerName: 'Description',
      field: 'description',
      editable: true,
      flex: 2,
      minWidth: 180,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: { maxLength: 500 }
    },
    {
      headerName: 'Qty (cases)',
      field: 'quantity',
      editable: true,
      width: 120,
      valueParser: (params) => params.newValue === '' ? '' : params.newValue,
      valueFormatter: (params) => params.value,
    },
    {
      headerName: 'Unit Price',
      field: 'unitPrice',
      editable: true,
      width: 130,
      valueFormatter: (params) => {
        const row = params.data || {};
        const cq = parseFloat(row?.caseQuantity);
        const hasCq = Number.isFinite(cq) && cq > 0;
        const unitEach = parseFloat(row?.unitPrice) || 0;
        const display = row?.priceBasis === 'case' && hasCq
          ? unitEach * cq
          : unitEach;
        return currencyFormatter(display);
      },
      valueSetter: (params) => {
        // Convert display value back to per-each storage
        const raw = parseFloat(params.newValue);
        const row = params.data || {};
        const cq = parseFloat(row?.caseQuantity);
        const hasCq = Number.isFinite(cq) && cq > 0;
        const perEach = (row?.priceBasis === 'case' && hasCq) ? (raw / cq) : raw;
        params.data.unitPrice = String(perEach);
        return true;
      }
    },
    {
      headerName: 'Case Qty',
      field: 'caseQuantity',
      editable: true,
      width: 110,
      valueParser: (params) => params.newValue === '' ? '' : params.newValue,
    },
    {
      headerName: 'Unit Size',
      field: 'unitSize',
      editable: true,
      width: 140,
    },
    {
      headerName: 'Basis',
      field: 'priceBasis',
      editable: true,
      width: 110,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: ['each', 'case'] },
      valueFormatter: (params) => params.value === 'case' ? 'Per case' : 'Per each',
    },
    {
      headerName: 'VAT Rate',
      field: 'vatRate',
      editable: true,
      width: 110,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: vatValues },
      valueFormatter: (params) => `${params.value || 0}%`,
    },
    {
      headerName: 'VAT Code',
      field: 'vatCode',
      editable: false,
      width: 100,
      valueFormatter: (params) => params.value || rateToCode(params.data?.vatRate),
    },
    {
      headerName: 'Net',
      field: 'netTotal',
      editable: false,
      width: 120,
      valueFormatter: (params) => currencyFormatter(params.value),
    },
    {
      headerName: 'VAT Amt',
      field: 'vatAmount',
      editable: false,
      width: 120,
      valueFormatter: (params) => currencyFormatter(params.value),
    },
    {
      headerName: 'Total',
      field: 'total',
      editable: false,
      width: 130,
      valueFormatter: (params) => currencyFormatter(params.value),
    },
  ], [vatValues]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    flex: 1,
    minWidth: 100,
    suppressMenu: true,
  }), []);

  const pinnedBottomRowData = useMemo(() => {
    const net = rowData.reduce((s, r) => s + (parseFloat(r?.netTotal) || 0), 0);
    const vat = rowData.reduce((s, r) => s + (parseFloat(r?.vatAmount) || 0), 0);
    const netRounded = round2(net);
    const vatRounded = round2(vat);
    const total = round2(netRounded + vatRounded);
    return [{
      description: 'Totals',
      netTotal: netRounded,
      vatAmount: vatRounded,
      total,
    }];
  }, [rowData]);

  const quickFilter = (value) => {
    setQuickFilterText(value || '');
    gridRef.current?.api?.setQuickFilter?.(value || '');
  };

  const onCellValueChanged = (params) => {
    if (!params) return;
    const field = params.colDef.field;
    const updated = recalcLine({ ...params.data, [field]: params.data[field] });
    params.node.setData(updated);
    syncRowsFromGrid();
  };

  return (
    <div className="h-full flex flex-col bg-card" style={{ height: '100%' }}>
      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Icon name="Grid" size={18} className="text-primary" />
          <div className="text-sm font-medium text-foreground">Bulk Edit (Grid)</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={quickFilterText}
            onChange={(e) => quickFilter(e.target.value)}
            placeholder="Quick filter"
            className="px-2 py-1 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => gridRef.current?.api?.refreshCells({ force: true })}
            iconName="RefreshCw"
            title="Refresh grid"
          />
        </div>
      </div>
      <div
        className="flex-1 ag-theme-alpine min-h-[500px] w-full"
        style={{ height: '100%', width: '100%' }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows
          stopEditingWhenCellsLoseFocus={true}
          onCellValueChanged={onCellValueChanged}
          suppressAggFuncInHeader
          rowSelection="multiple"
          pinnedBottomRowData={pinnedBottomRowData}
          theme="legacy"
          getRowId={(params) =>
            String(
              params.data?._rowId ??
              params.data?.id ??
              params.data?.description ??
              params.rowIndex
            )
          }
          overlayNoRowsTemplate="<span class='text-sm text-muted-foreground'>No line items found</span>"
        />
      </div>
    </div>
  );
};

export default BulkLineItemsGrid;
